import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import classnames from 'classnames';
import { FieldArray, Field } from 'formik';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import IconSunny from 'assets/images/icon-sunny.svg';
import IconMoon from 'assets/images/icon-moon.svg';
import DatePickerInput from 'components/DatePickerInput';
import { getProjectName, checkUserRole } from 'helpers/_helpers';
import styles from './styles';
import { isIOS, Option } from 'components/Option';
import LocationCreator from './components/LocationCreator';
import { AlternateContacts } from './components/AlternateContacts/AlternateContacts';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MenuItem from '@material-ui/core/MenuItem';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { parse, startOfDay } from 'date-fns';
import { SearchSelect } from 'lib';
import DayInput from 'components/DayInput';
import DurationInput from 'components/DurationInput';
import TimeInput from 'components/TimeInput';


const SheetDetails = ({
  classes,
  contractors,
  allProjects,
  projects,
  projectLocations,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setValues,
  handleSelectSearchProject,
  handleSelectContractor,
  handleSelectProject,
  handleChangeTemperature,
  type,
  handleChangeStartTime,
  handleChangeShift,
  sheet,
  handleSelectLocation,
  handleRemoveLocation,
  handleAppendLocation,
  locationEditor,
  rallyPointEditor,
  handleLocationEditor,
  handleRallyPointEditor,
  setLocations,
  contractorAlternateContacts,
  setContractorAlternateContacts,
  documents,
  calendarStartOfWeek,
  calendarEndOfWeek,
  tenantLocationId,
  setTenantLocationId,
}) => {
  const matches = useMediaQuery('(max-width: 999px)');
  const getTimeFormatHook = useTimeSettingsFormat();
  const settings = useSelector((state) => state.personalProfile?.organizationLocation?.settings);
  const activeLocations = useSelector((state) => state.personalProfile.activeLocations);
  const { dateFormat } = useDateSettingsFormat();
  
  const rallyPointOptions = useMemo(() => (
    projectLocations.filter((location) => (
      location.type === 'rallyPoint' && !values.rallyPoints.find((loc) => location._id === loc)
    ))
  ), [projectLocations, values.rallyPoints])
  const locationOptions = useMemo(() => (
    projectLocations.filter((location) => (
      location.type === 'projectLocation' && !values.locations.find((loc) => location._id === loc)
    ))
  ), [projectLocations, values.locations])

  const formatLocationName = (location) => {
    const a = location.address;
    const b = location.zipcode ? `, ${location.zipcode}` : '';
    const c = location.name ? ` - ${location.name}` : '';
    return `${a}${b}${c}`;
  };

  const isFieldTechnician = checkUserRole('Field Technician');

  const handleTenantLocationChange = (e) => {
    setTenantLocationId(e.target.value);
  }

  const allMappedProjects = useMemo(() => {
    return allProjects.map((project) => ({
      _id: project._id,
      contractorId: project.contractorId,
      title: getProjectName(project, { jobNumber: true }),
    }))
  }, [allProjects]);

  const setDate = (value) => {
    setFieldValue('startDate', startOfDay(parse(value, dateFormat, new Date())));
    setFieldValue('gridDate', value);
  };

  const setParts = (parts) => setValues({ ...values, ...parts });

  const hasError = (...names) => names.some((name) => errors[name]);

  const durationProps = (name, digits = 1) => ({
    value: { hours: values[`${name}Hours`], minutes: values[`${name}Minutes`] },
    onChange: ({ hours, minutes }) =>
      setParts({
        [`${name}Hours`]: String(hours),
        [`${name}Minutes`]: String(minutes).padStart(digits, '0'),
      }),
    error: hasError(`${name}Hours`, `${name}Minutes`),
  });

  const durationError = (name) => {
    const error = errors[`${name}Hours`] || errors[`${name}Minutes`];
    return error ? <div className={classes.error}>{error}</div> : null;
  };

  return (
    <>
      <Grid container justifyContent="flex-end" spacing={4}>
        {isFieldTechnician && type === 'create' && (
          <Grid item xs={12} className={classes.zeroPaddingTop}>
            <label htmlFor="contractorId">
              <Typography variant="body1" color="textSecondary">
                Location<span className={classes.requiredSign}>*</span>:
              </Typography>

              <div style={{ position: 'relative' }}>
                <TextField
                  select
                  id="tenantLocationId"
                  className={classes.textField}
                  value={tenantLocationId}
                  onChange={handleTenantLocationChange}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'Company Location' }}
                  SelectProps={{
                    native: isIOS,
                  }}
                  fullWidth
                >
                  {activeLocations.map(location => (
                    <Option key={location._id} value={location._id}>
                      {location.name}
                    </Option>
                  ))}
                </TextField>
              </div>
            </label>
          </Grid>
        )}
        <Grid item container xs={12} className={classnames(classes.zeroPaddingTop)}>
          <Grid item xs={12} lg={8} className={classnames(classes.zeroPaddingTop, classes.paddingRightMedium)}>
            <label htmlFor="searchProject">
              <Typography variant="body1" color="textSecondary">
                Search:
              </Typography>

              <div style={{ position: 'relative' }}>
                <SearchSelect
                  value={values.searchProject}
                  onChange={handleSelectSearchProject(setFieldValue)}
                  options={allMappedProjects}
                  getOptionLabel={(option) => option.title || ''}
                  inputProps={{
                    id: 'searchProject',
                    name: 'searchProject',
                    placeholder: 'Search by Job Number, Project ID, or Project Name',
                  }}
                />
              </div>
              {errors.contractorId && touched.contractorId ? (
                <div className={classes.error}>{errors.contractorId}</div>
              ) : null}
            </label>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={8} className={classes.zeroPaddingTop}>
          <label htmlFor="contractorId">
            <Typography variant="body1" color="textSecondary">
              Contractor<span className={classes.requiredSign}>*</span>:
            </Typography>

            <div style={{ position: 'relative' }}>
              <SearchSelect
                value={values.contractorId}
                onChange={handleSelectContractor(setFieldValue)}
                options={contractors}
                getOptionLabel={(option) => option.name || ''}
                error={Boolean(errors.contractorId)}
                inputProps={{ id: 'contractorId', name: 'contractorId', 'aria-label': 'Contractor' }}
              />
            </div>
            {errors.contractorId && touched.contractorId ? (
              <div className={classes.error}>{errors.contractorId}</div>
            ) : null}
          </label>
        </Grid>
        <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
          <label htmlFor="forceAccount">
            <Typography variant="body1" color="textSecondary">
              Time & Material:
            </Typography>
            <TextField
              select
              id="forceAccount"
              name="forceAccount"
              className={classes.textField}
              value={values.forceAccount}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              inputProps={{ 'aria-label': 'Time & Material' }}
              SelectProps={{
                native: isIOS,
              }}
              fullWidth
            >
              <Option key={0} value="No">
                No
              </Option>
              <Option key={1} value="Full">
                Full
              </Option>
              <Option key={2} value="Partial">
                Partial
              </Option>
            </TextField>
          </label>
        </Grid>
        {values.forceAccount === 'Partial' && (
          <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
            <Typography variant="body1" color="textSecondary">
              T&M Time:
            </Typography>
            <div className={classes.pickers}>
              <DurationInput
                title="T&M time"
                maxHours={48}
                minuteStep={5}
                {...durationProps('forceAccount', 2)}
              />
            </div>
            {durationError('forceAccount')}
          </Grid>
        )}
        <Grid item xs={12} lg={8} className={classes.zeroPaddingTop}>
          <label htmlFor="projectId">
            <Typography variant="body1" color="textSecondary">
              Project<span className={classes.requiredSign}>*</span>:
            </Typography>
            <SearchSelect
              value={values.projectId}
              onChange={handleSelectProject(setFieldValue)}
              options={projects.slice().sort((a, b) => a.route.localeCompare(b.route))}
              getOptionLabel={(option) => getProjectName(option, { jobNumber: true })}
              error={Boolean(errors.projectId)}
              inputProps={{ id: 'projectId', name: 'projectId', 'aria-label': 'Project' }}
            />
            {errors.projectId && touched.projectId ? (
              <div className={classes.error}>{errors.projectId}</div>
            ) : null}
          </label>
        </Grid>
        {type === 'create' && (
          <Grid item xs={12} md={4} className={classnames(classes.zeroPaddingTop)}>
            <label htmlFor="startDate">
              <Typography variant="body1" color="textSecondary">
                Date:
              </Typography>
              <DatePickerInput
                selectedDay={values.startDate}
                setFieldValue={(_, value) => setDate(value)}
                field="startDate"
                alignRight
                fullWidth
                minDate={calendarStartOfWeek}
                maxDate={calendarEndOfWeek}
              />
            </label>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={12}
          className={classnames(classes.zeroPaddingTop, classes.hideForDesktop)}
        >
          <label htmlFor="gridDate">
            <Typography variant="body1" color="textSecondary">
              Grid Date:
            </Typography>
            <DatePickerInput
              selectedDay={values.gridDate}
              setFieldValue={setFieldValue}
              field="gridDate"
              alignRight
              fullWidth
              minDate={calendarStartOfWeek}
              maxDate={calendarEndOfWeek}
            />
          </label>
        </Grid>
        {!!values.contractorId && (
          <>
            <Grid item xs={12} className={classes.zeroPaddingTop}>
              <Divider />
            </Grid>

            <AlternateContacts
              CloseIcon={CloseIcon}
              classes={classes}
              values={values}
              setFieldValue={setFieldValue}
              contractorAlternateContacts={contractorAlternateContacts}
              setContractorAlternateContacts={setContractorAlternateContacts}
            />
          </>
        )}

        <Grid item xs={12} className={classes.zeroPaddingTop} style={{ marginBottom: 15 }}>
          <Divider />
        </Grid>
      </Grid>

      <Grid container xs={12} lg={12} className={classnames(classes.zeroPaddingTop)}>
        <Grid
          item
          xs={12}
          lg={9}
          className={classnames(classes.zeroPaddingTop, classes.flexColumn)}
        >
          {values.rallyPoints.length ? (
            values.rallyPoints.map((locationId, index) => (
              <Grid container xs={12} lg={11} className={classes.zeroPaddingTop}>
                <div
                  className={classnames(classes.flexColumn, classes.paddingBottom)}
                  key={`${locationId}_${index}`}
                >
                  <Typography variant="body1" color="textSecondary">
                    Rally Point{values.rallyPoints.length === 1 ? null : ` ${index + 1}`}:
                  </Typography>
                  <div className={classnames(classes.flexBox, classes.fullWidth)}>
                    <div className={classes.flexGrow}>
                      <SearchSelect
                        value={locationId}
                        options={[
                          projectLocations.find((pLoc) => pLoc._id === locationId),
                          ...rallyPointOptions,
                        ].filter(Boolean)}
                        getOptionLabel={(option) => formatLocationName(option)}
                        error={Boolean(errors.rallyPoints?.[index])}
                        inputProps={{ id: `rallyPoints[${index}]`, 'aria-label': 'Rally Point' }}
                        onChange={handleSelectLocation(
                          setFieldValue,
                          values.rallyPoints,
                          index,
                          'rallyPoint'
                        )}
                        footer={
                          values?.projectId && (
                            <Button
                              variant="text"
                              className={classes.primaryTextButton}
                              onClick={() => handleRallyPointEditor(index)}
                              disableRipple
                            >
                              + Create New Rally Point
                            </Button>
                          )
                        }
                      />
                    </div>
                    <IconButton
                      className={classes.deleteLocationButton}
                      onClick={handleRemoveLocation(
                        setFieldValue,
                        values.rallyPoints,
                        index,
                        'rallyPoints'
                      )}
                      aria-label="Delete"
                      disableTouchRipple
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
                {index + 1 === values.rallyPoints.length &&
                  values.rallyPoints &&
                  projectLocations.filter((location) => location.type === 'rallyPoint').length >
                    values.rallyPoints.length &&
                  !rallyPointEditor && (
                    <div className={classes.flexBox}>
                      <Button
                        variant="text"
                        className={classes.primaryTextButton}
                        onClick={handleAppendLocation(
                          setFieldValue,
                          values.rallyPoints,
                          'rallyPoints'
                        )}
                        disableRipple
                      >
                        + Add Rally Point
                      </Button>
                    </div>
                  )}
              </Grid>
            ))
          ) : (
            <div className={classnames(classes.flexColumn, classes.paddingBottom)}>
              <Typography variant="body1" color="textSecondary">
                Rally Point:
              </Typography>
              <div className={classnames(classes.flexBox, classes.fullWidth)}>
                <TextField
                  select
                  id="rallyPointsId"
                  name="rallyPointsId"
                  className={classes.textField}
                  value=""
                  onChange={(e) => handleSelectLocation(
                    setFieldValue,
                    values.rallyPoints,
                    0,
                    'rallyPoint'
                  )(e.target.value)}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'Location' }}
                  SelectProps={{
                    native: isIOS,
                  }}
                  fullWidth
                >
                  <Option key={`index_0`} value="" />
                  {projectLocations
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((location) => (
                      <Option key={location._id} value={location._id}>
                        {formatLocationName(location)}
                      </Option>
                    ))}
                </TextField>
                <IconButton
                  className={classes.deleteLocationButton}
                  onClick={handleRemoveLocation(
                    setFieldValue,
                    values.rallyPoints,
                    0,
                    'rallyPoints'
                  )}
                  aria-label="Delete"
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          lg={3}
          className={classnames(classes.zeroPaddingTop, classes.flexColumn)}
        >
          <Grid
            item
            xs={12}
            md={12}
            className={classnames(classes.zeroPaddingTop, classes.hideForMob)}
          >
            <label htmlFor="gridDate">
              <Typography variant="body1" color="textSecondary">
                Grid Date:
              </Typography>
              <DatePickerInput
                selectedDay={values.gridDate}
                setFieldValue={setFieldValue}
                field="gridDate"
                alignRight
                fullWidth
                minDate={calendarStartOfWeek}
                maxDate={calendarEndOfWeek}
              />
            </label>
          </Grid>
        </Grid>
        {rallyPointEditor ? (
          <LocationCreator
            locationEditor={rallyPointEditor}
            values={values}
            handleLocationEditor={handleRallyPointEditor}
            setFieldValue={setFieldValue}
            setLocations={setLocations}
            type={'rallyPoint'}
            fieldName={'rallyPoints'}
          />
        ) : null}
      </Grid>
      <Grid container spacing={4} className={classes.paddingBottomBig} style={{ marginTop: 5 }}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <Grid item xs={12} lg={12} className={classnames(classes.zeroPaddingTop, classes.flexColumn)}>
        <Grid
          item
          xs={12}
          lg={9}
          className={classnames(classes.zeroPaddingTop, classes.flexColumn)}
        >
          {values.locations.length ? (
            values.locations.map((locationId, index) => (
              <Grid container xs={12} lg={11} className={classes.zeroPaddingTop}>
                <div
                  className={classnames(classes.flexColumn, classes.paddingBottom)}
                  key={`${locationId}_${index}`}
                >
                  <Typography variant="body1" color="textSecondary">
                    Project Location{values.locations.length === 1 ? null : ` ${index + 1}`}:
                  </Typography>
                  <div className={classnames(classes.flexBox, classes.fullWidth)}>
                    <div className={classes.flexGrow}>
                      <SearchSelect
                        value={locationId}
                        options={[
                          projectLocations.find((pLoc) => pLoc._id === locationId),
                          ...locationOptions,
                        ].filter(Boolean)}
                        getOptionLabel={(option) => formatLocationName(option)}
                        error={Boolean(errors.locations?.[index])}
                        inputProps={{ id: `locations[${index}]`, 'aria-label': 'Project Location' }}
                        onChange={handleSelectLocation(
                          setFieldValue,
                          values.locations,
                          index,
                          'projectLocation'
                        )}
                        footer={
                          values?.projectId && (
                            <Button
                              variant="text"
                              className={classes.primaryTextButton}
                              onClick={() => handleLocationEditor(index)}
                              disableRipple
                            >
                              + Create New Project Location
                            </Button>
                          )
                        }
                      />
                    </div>
                    <IconButton
                      className={classes.deleteLocationButton}
                      onClick={handleRemoveLocation(
                        setFieldValue,
                        values.locations,
                        index,
                        'locations'
                      )}
                      aria-label="Delete"
                      disableTouchRipple
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
                {index + 1 === values.locations.length &&
                  values.locations &&
                  projectLocations.filter((location) => location.type === 'projectLocation')
                    .length > values.locations.length &&
                  !locationEditor && (
                    <div className={classes.flexBox}>
                      <Button
                        variant="text"
                        className={classes.primaryTextButton}
                        onClick={handleAppendLocation(setFieldValue, values.locations, 'locations')}
                        disableRipple
                      >
                        + Add Project Location
                      </Button>
                    </div>
                  )}
              </Grid>
            ))
          ) : (
            <div className={classnames(classes.flexColumn, classes.paddingBottom)}>
              <Typography variant="body1" color="textSecondary">
                Project Location:
              </Typography>
              <div className={classnames(classes.flexBox, classes.fullWidth)}>
                <TextField
                  select
                  id="locationId"
                  name="locationId"
                  className={classes.textField}
                  value=""
                  onChange={(e) => handleSelectLocation(
                    setFieldValue,
                    values.locations,
                    0,
                    'projectLocation'
                  )(e.target.value)}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'Location' }}
                  SelectProps={{
                    native: isIOS,
                  }}
                  fullWidth
                >
                  <Option key={`index_0`} value="" />
                  {projectLocations
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((location) => (
                      <Option key={location._id} value={location._id}>
                        {formatLocationName(location)}
                      </Option>
                    ))}
                </TextField>
                <IconButton
                  className={classes.deleteLocationButton}
                  onClick={handleRemoveLocation(setFieldValue, values.locations, 0, 'locations')}
                  aria-label="Delete"
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          )}
        </Grid>

        {locationEditor ? (
          <LocationCreator
            locationEditor={locationEditor}
            values={values}
            handleLocationEditor={handleLocationEditor}
            setFieldValue={setFieldValue}
            setLocations={setLocations}
            type={'projectLocation'}
            fieldName={'locations'}
          />
        ) : null}
      </Grid>
      <Grid container spacing={4} className={classes.paddingBottomBig} style={{ marginTop: 5 }}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      {!!documents?.length && (
        <>
          <Grid
            item
            xs={12}
            lg={12}
            className={classnames(classes.zeroPaddingTop, classes.paddingBottom)}
          >
            <Typography variant="body1" color="textSecondary">
              Documents:
            </Typography>
          </Grid>
          <Grid
            container
            xs={12}
            lg={12}
            className={classnames(classes.zeroPaddingTop)}
            spacing={matches ? 0 : 3}
          >
            {documents?.length === 1 ? (
              <Grid item xs={12} lg={8}>
                <span>
                  <Link
                    variant="body1"
                    className={classes.primaryAdd}
                    href={documents[0].url}
                    target="_blank"
                  >
                    {documents[0].name}
                  </Link>
                  &nbsp;
                </span>
              </Grid>
            ) : (
              <Grid item xs={12} lg={8} className={classnames(classes.zeroPaddingTop)}>
                <TextField
                  select
                  id="documentStorageId"
                  name="documentStorageId"
                  className={classes.textFieldSelect}
                  variant="outlined"
                  onChange={(e) => {
                    e.preventDefault();
                  }}
                  label={'Select the document to open'}
                  inputProps={{
                    shrink: true,
                    'aria-label': 'Shift',
                  }}
                  SelectProps={{
                    displayEmpty: true,
                  }}
                  fullWidth
                  size="small"
                  value={''}
                >
                  {[...documents]
                    .sort((a, b) => a?.name?.localeCompare(b?.name))
                    .map((doc) => (
                      <MenuItem
                        key={doc.name}
                        value={doc.name}
                        onClick={(e) => {
                          window.open(doc.url, '_black', 'noreferrer');
                        }}
                      >
                        {doc.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={4} className={classes.paddingBottomBig} style={{ marginTop: 5 }}>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        </>
      )}
      <Grid container spacing={4}>
        <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
          <label htmlFor="timeOfDay">
            <Typography variant="body1" color="textSecondary">
              Shift:
            </Typography>
            <TextField
              select
              id="timeOfDay"
              name="timeOfDay"
              className={classes.textField}
              value={values.timeOfDay}
              onChange={handleChangeShift(setValues, values)}
              onBlur={handleBlur}
              variant="outlined"
              inputProps={{ 'aria-label': 'Shift' }}
              SelectProps={{
                native: isIOS,
              }}
              fullWidth
            >
              <Option key={0} value="Day">
                {isIOS ? '🌤 ' : <img src={IconSunny} alt="sunny" className={classes.shiftImage} />}
                Day
              </Option>
              <Option key={1} value="Night">
                {isIOS ? '🌙 ' : <img src={IconMoon} alt="moon" className={classes.shiftImage} />}
                Night
              </Option>
            </TextField>
          </label>
        </Grid>
        <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
          <label>
            <Typography variant="body1" color="textSecondary">
              Start Time:
            </Typography>
          </label>
          <div className={classes.pickers}>
            <TimeInput
              value={{
                hour: values.startTimeHours,
                minute: values.startTimeMinutes,
                amPm: values.startTimeDayPart,
              }}
              onChange={handleChangeStartTime(setValues, values)}
              error={hasError('startTimeHours', 'startTimeMinutes', 'startTimeDayPart')}
            />
          </div>
          {errors.startTimeHours ? (
            <div className={classes.error}>{errors.startTimeHours}</div>
          ) : null}
        </Grid>
        <Grid container direction="row" alignItems="center" item xs={12} lg={4}>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <Field
              type="checkbox"
              name="travelTimeOnly"
              render={({ field }) => {
                return (
                  <Checkbox
                    color="primary"
                    {...field}
                    checked={field.value}
                    className={classes.primaryCheckbox}
                  />
                );
              }}
            />
            <Typography variant="body1" className={classes.primaryAdd}>
              {values?.isRainPayEnabled ? 'Omit Job Time' : 'Travel Time Only'}
            </Typography>
          </label>
        </Grid>
        {type === 'create' ? (
          <>
            <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
              <label>
                <Typography variant="body1" color="textSecondary">
                  On Job Time:
                </Typography>
              </label>
              <div className={classes.pickers}>
                <TimeInput
                  value={{
                    hour: values.onJobHours,
                    minute: values.onJobMinutes,
                    amPm: values.onJobDayPart,
                  }}
                  onChange={({ hour, minute, amPm }) =>
                    setParts({ onJobHours: hour, onJobMinutes: minute, onJobDayPart: amPm })
                  }
                  error={hasError('onJobHours', 'onJobMinutes', 'onJobDayPart')}
                  disabled={values.travelTimeOnly}
                />
              </div>
              {errors.onJobHours ? (
                <div className={classes.error}>{errors.onJobHours}</div>
              ) : null}
            </Grid>
            <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
              <label>
                <Typography variant="body1" color="textSecondary">
                  Estimated Time:
                </Typography>
              </label>
              <div className={classes.pickers}>
                <DurationInput
                  title="Estimated time"
                  maxHours={getTimeFormatHook().is12Format ? 12 : 23}
                  minuteStep={5}
                  disabled={values.travelTimeOnly}
                  {...durationProps('estimated', 2)}
                />
              </div>
              {durationError('estimated')}
            </Grid>
            {settings?.timeclock && settings?.miscStipend ? (
              <Grid container direction="row" alignItems="center" item xs={12} lg={4}>
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <Field
                    type="checkbox"
                    name="isRainPayEnabled"
                    render={({ field }) => {
                      return (
                        <Checkbox
                          color="primary"
                          {...field}
                          checked={field.value}
                          className={classes.primaryCheckbox}
                        />
                      );
                    }}
                  />
                  <Typography variant="body1" className={classes.primaryAdd}>
                    Misc. Stipend
                  </Typography>
                </label>
              </Grid>
            ) : (
              <Grid item xs={0} lg={4} />
            )}
          </>
        ) : (
          <Grid item xs={12} className={classes.zeroPaddingTop}>
            <div>
              <Typography variant="body1" color="textSecondary">
                On Job Time:
              </Typography>
              <Grid container>
                <Grid item xs={12} md={5}>
                  <div className={classes.pickers}>
                    <DayInput
                      value={values.dayStart}
                      onChange={(dayStart) => setParts({ dayStart })}
                      baseDate={sheet.createdAt}
                      error={hasError('dayStart')}
                    />
                    <TimeInput
                      value={{
                        hour: values.hourStart,
                        minute: values.minuteStart,
                        amPm: values.amPmStart,
                      }}
                      onChange={({ hour, minute, amPm }) =>
                        setParts({ hourStart: hour, minuteStart: minute, amPmStart: amPm })
                      }
                      error={hasError('hourStart', 'minuteStart', 'amPmStart')}
                    />
                  </div>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  className={classnames(classes.flexBox, classes.spaceAround)}
                >
                  <Typography variant="body1" color="textSecondary">
                    &mdash;
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <div className={classes.pickers}>
                    <DayInput
                      value={values.dayEnd}
                      onChange={(dayEnd) => setParts({ dayEnd })}
                      baseDate={sheet.createdAt}
                      error={hasError('dayEnd')}
                    />
                    <TimeInput
                      value={{
                        hour: values.hourEnd,
                        minute: values.minuteEnd,
                        amPm: values.amPmEnd,
                      }}
                      onChange={({ hour, minute, amPm }) =>
                        setParts({ hourEnd: hour, minuteEnd: minute, amPmEnd: amPm })
                      }
                      error={hasError('hourEnd', 'minuteEnd', 'amPmEnd')}
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          </Grid>
        )}
        <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
          <div>
            <Typography variant="body1" color="textSecondary">
              Travel Time to Job:
            </Typography>
            <div className={classes.pickers}>
              <DurationInput title="Travel time" maxHours={24} {...durationProps('travelTimeTo')} />
            </div>
            {durationError('travelTimeTo')}
          </div>
        </Grid>
        <Grid item xs={12} lg={8} className={classes.zeroPaddingTop}>
          <div>
            <Typography variant="body1" color="textSecondary">
              Travel Time from Job:
            </Typography>
            <div className={classes.pickers}>
              <DurationInput
                title="Travel time"
                maxHours={24}
                {...durationProps('travelTimeFrom')}
              />
            </div>
            {durationError('travelTimeFrom')}
          </div>
        </Grid>
        {values?.isRainPayEnabled && settings?.timeclock && settings?.miscStipend && (
          <>
            <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
              <div>
                <Typography variant="body1" color="textSecondary">
                  Misc. Stipend:
                </Typography>
                <div className={classes.pickers}>
                  <DurationInput
                    title="Misc. stipend"
                    maxHours={24}
                    {...durationProps('rainPay')}
                  />
                </div>
                {durationError('rainPay')}
              </div>
            </Grid>
            <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
              <div>
                <Typography variant="body1" color="textSecondary">
                  Misc. Stipend Description:
                </Typography>
                <div className={classnames(classes.alignTop)}>
                  <div>
                    <TextField
                      id="rainPayDescription"
                      name="rainPayDescription"
                      className={classes.textField}
                      value={values.rainPayDescription}
                      error={
                        Boolean(errors.rainPayDescription) && Boolean(touched.rainPayDescription)
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant="outlined"
                      fullWidth
                    />
                    {errors.rainPayDescription && touched.rainPayDescription ? (
                      <div className={classes.error}>{errors.rainPayDescription}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            </Grid>
          </>
        )}
      </Grid>
      <Grid container spacing={4} className={classes.paddingBottomBig}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={12} lg={4} className={classes.zeroPaddingTop}>
          <Grid container spacing={4}>
            <FieldArray
              name="temperatures"
              render={(arrayHelpers) =>
                values.temperatures.map((temperature, index) => (
                  <Grid
                    item
                    xs={12}
                    lg={6}
                    key={index}
                    className={classnames(index > 1 && classes.zeroPaddingTop)}
                  >
                    {index === 0 && (
                      <Typography variant="body1" color="textSecondary">
                        Temperature:
                      </Typography>
                    )}
                    {index === 1 && <div className={classes.constantHeight} />}
                    <TextField
                      id={`temperatures[${index}]`}
                      name={`temperatures[${index}]`}
                      className={classes.textField}
                      value={values.temperatures[index]}
                      error={
                        Boolean(errors[`temperature${index}`] && errors[`temperature${index}`]) &&
                        Boolean(touched.temperatures && touched.temperatures[index])
                      }
                      onChange={handleChangeTemperature(setFieldValue, index, values, arrayHelpers)}
                      onBlur={handleBlur}
                      variant="outlined"
                      fullWidth
                      inputProps={{ maxLength: 3 }}
                    />
                  </Grid>
                ))
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={12} lg={2} className={classes.zeroPaddingTop}>
          <label htmlFor="humidity">
            <Typography variant="body1" color="textSecondary">
              Humidity:
            </Typography>
            <TextField
              id="humidity"
              name="humidity"
              className={classes.textField}
              value={values.humidity}
              error={Boolean(errors.humidity) && Boolean(touched.humidity)}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              inputProps={{ 'aria-label': 'Humidity' }}
              fullWidth
            />
            {errors.humidity && touched.humidity ? (
              <div className={classes.error}>{errors.humidity}</div>
            ) : null}
          </label>
        </Grid>
        <Grid item xs={12} lg={6} className={classes.zeroPaddingTop}>
          <label htmlFor="weatherNotes">
            <Typography variant="body1" color="textSecondary">
              Weather Notes:
            </Typography>
            <TextField
              id="weatherNotes"
              name="weatherNotes"
              className={classes.textField}
              value={values.weatherNotes}
              error={Boolean(errors.weatherNotes) && Boolean(touched.weatherNotes)}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              inputProps={{ 'aria-label': 'Weather notes' }}
              fullWidth
            />
            {errors.weatherNotes && touched.weatherNotes ? (
              <div className={classes.error}>{errors.weatherNotes}</div>
            ) : null}
          </label>
        </Grid>
      </Grid>
      <Grid container spacing={4} className={classes.paddingBottomBig}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={12} className={classes.zeroPaddingTop}>
          <label htmlFor="notes">
            <Typography variant="body1" color="textSecondary">
              Notes:
            </Typography>
            <TextField
              id="notes"
              name="notes"
              className={classes.textField}
              value={values.notes}
              error={Boolean(errors.notes) && Boolean(touched.notes)}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              inputProps={{ 'aria-label': 'Notes' }}
              fullWidth
            />
            {errors.notes && touched.notes ? (
              <div className={classes.error}>{errors.notes}</div>
            ) : null}
          </label>
        </Grid>
        <Grid item xs={12} className={classes.zeroPaddingTop}>
          <label htmlFor="schedulerNotes">
            <Typography variant="body1" color="textSecondary">
              Internal Notes:
            </Typography>
            <TextField
              id="schedulerNotes"
              name="schedulerNotes"
              className={classes.textField}
              value={values.schedulerNotes}
              error={Boolean(errors.schedulerNotes) && Boolean(touched.schedulerNotes)}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              inputProps={{ 'aria-label': 'Internal notes' }}
              fullWidth
              disabled={
                !(
                  checkUserRole('Dispatcher') ||
                  checkUserRole('Admin') ||
                  checkUserRole('Super Admin')
                )
              }
            />
            {errors.schedulerNotes && touched.schedulerNotes ? (
              <div className={classes.error}>{errors.schedulerNotes}</div>
            ) : null}
          </label>
        </Grid>
      </Grid>
    </>
  );
};

SheetDetails.propTypes = {
  classes: propTypes.object.isRequired,
  contractors: propTypes.array.isRequired,
  projects: propTypes.array.isRequired,
  projectLocations: propTypes.array.isRequired,
  values: propTypes.object.isRequired,
  errors: propTypes.object.isRequired,
  touched: propTypes.object.isRequired,
  handleChange: propTypes.func.isRequired,
  handleBlur: propTypes.func.isRequired,
  setFieldValue: propTypes.func.isRequired,
  setValues: propTypes.func.isRequired,
  handleSelectContractor: propTypes.func.isRequired,
  handleSelectProject: propTypes.func.isRequired,
  handleChangeTemperature: propTypes.func.isRequired,
  type: propTypes.string.isRequired,
  handleChangeStartTime: propTypes.func.isRequired,
  handleChangeShift: propTypes.func.isRequired,
  sheet: propTypes.object,
  handleSelectLocation: propTypes.func.isRequired,
  handleRemoveLocation: propTypes.func.isRequired,
  handleAppendLocation: propTypes.func.isRequired,
};

export default withStyles(styles)(SheetDetails);
